import axios from "axios";
import { cacheGet, cacheSet, enqueue, tmpId } from "./offline";
import { openDB } from "idb";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("stellar_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// URLs whose GETs we want cached for offline reads
const CACHEABLE_GET = /^\/(services|customers|appointments|dashboard)/;
const QUEUEABLE = /^\/(appointments|customers)(\/|$)/;

function isNetworkError(err) {
  return !err.response && (err.code === "ERR_NETWORK" || err.message === "Network Error" || !navigator.onLine);
}

// Patch all cached list endpoints for a given entity type after a queued mutation.
async function patchCachedLists(entityRoot, patcher) {
  try {
    const db = await openDB("stellar_offline_v1");
    const tx = db.transaction("cache", "readwrite");
    const store = tx.objectStore("cache");
    const keys = await store.getAllKeys();
    for (const k of keys) {
      if (typeof k !== "string") continue;
      const path = k.split("?")[0];
      if (path === `/${entityRoot}` || path === `/appointments`) {
        const list = await store.get(k);
        if (Array.isArray(list)) {
          const next = patcher(list);
          if (next) await store.put(next, k);
        }
      }
    }
    await tx.done;
  } catch (_) { /* ignore */ }
}

api.interceptors.response.use(
  async (res) => {
    if (res.config.method === "get") {
      const url = (res.config.url || "").split("?")[0];
      if (CACHEABLE_GET.test(url)) {
        try { await cacheSet(res.config.url, res.data); } catch (_) { /* ignore */ }
      }
    }
    return res;
  },
  async (err) => {
    const cfg = err.config || {};
    const method = (cfg.method || "get").toLowerCase();
    const url = (cfg.url || "").split("?")[0];

    // Auth expired
    if (err.response?.status === 401) {
      localStorage.removeItem("stellar_token");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    // Offline GET → serve from cache
    if (method === "get" && isNetworkError(err) && CACHEABLE_GET.test(url)) {
      const cached = await cacheGet(cfg.url);
      if (cached !== undefined) {
        return { data: cached, status: 200, statusText: "OK (cache)", headers: {}, config: cfg, cached: true };
      }
    }

    // Offline mutation → enqueue
    if (["post", "put", "delete"].includes(method) && isNetworkError(err) && QUEUEABLE.test(url)) {
      const entity = url.startsWith("/appointments") ? "appointment" : "customer";
      const match = url.match(/^\/(appointments|customers)\/([^/]+)/);
      const id = match ? match[2] : null;
      const payload = cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : {};
      let op;
      let synthetic;
      if (method === "post") {
        const localId = tmpId();
        op = { op: "create", entity, payload: { ...payload, id: localId, _local: true } };
        synthetic = { ...payload, id: localId, _local: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), photos: [] };
      } else if (method === "put" && id) {
        op = { op: "update", entity, id, payload, client_updated_at: new Date().toISOString() };
        synthetic = { ...payload, id, _local: true, updated_at: new Date().toISOString() };
      } else if (method === "delete" && id) {
        op = { op: "delete", entity, id };
        synthetic = { ok: true, id, _local: true };
      }
      if (op) {
        await enqueue(op);
        window.dispatchEvent(new CustomEvent("stellar:queue-changed"));
        return { data: synthetic, status: 202, statusText: "Queued", headers: {}, config: cfg, queued: true };
      }
    }

    return Promise.reject(err);
  }
);

export const fileUrl = (path) => {
  const token = localStorage.getItem("stellar_token");
  return `${API}/files?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(token || "")}`;
};

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Fehler";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}
